import { Query, QueryClient } from "@tanstack/react-query";

type QueryPredicate = (query: Query) => boolean;

/**
 * Build a predicate that matches queries whose serialized key
 * contains a given URL fragment. This is consistent with how
 * the codebase constructs query keys via useApiQuery.
 */
export function urlPredicate(urlPart: string): QueryPredicate {
  return (query: Query) => {
    const key = query.queryKey[0];
    if (typeof key !== "string") return false;
    try {
      const config = JSON.parse(key);
      return config.url?.includes(urlPart) ?? false;
    } catch {
      return false;
    }
  };
}

interface OptimisticMutationOptions<TData> {
  queryClient: QueryClient;
  queryPredicate: QueryPredicate;
  updater: (oldData: TData) => TData;
  mutationFn: () => Promise<unknown>;
  onSettled?: () => void;
}

/**
 * Perform a mutation with automatic optimistic cache update and rollback.
 *
 * 1. Cancel in-flight queries so they don't overwrite our optimistic data
 * 2. Snapshot all matching cache entries (key + data)
 * 3. Apply `updater` optimistically
 * 4. Run `mutationFn`
 * 5. On error → restore each snapshot by key, do NOT invalidate
 * 6. On success → call `onSettled` to revalidate from server
 */
export async function optimisticMutation<TData>({
  queryClient,
  queryPredicate,
  updater,
  mutationFn,
  onSettled,
}: OptimisticMutationOptions<TData>): Promise<void> {
  await queryClient.cancelQueries({ predicate: queryPredicate });

  // Snapshot every matching cache entry before mutating
  const snapshots = queryClient.getQueriesData<TData>({ predicate: queryPredicate });

  // Apply the optimistic update
  queryClient.setQueriesData<TData>(
    { predicate: queryPredicate },
    (oldData) => (oldData === undefined ? oldData : updater(oldData)),
  );

  try {
    await mutationFn();
  } catch (error) {
    // Rollback: restore each entry by its exact query key
    for (const [queryKey, data] of snapshots) {
      queryClient.setQueryData(queryKey, data);
    }
    // Do NOT call onSettled on error — the rollback is the final state.
    // Calling invalidate/refetch while offline would just put queries into
    // a loading/error state and clobber the rolled-back data.
    throw error;
  }

  // Only revalidate on success
  onSettled?.();
}

type ListOperation<T extends { id: string }> =
  | { type: "update"; id: string; changes: Partial<T> }
  | { type: "remove"; id: string }
  | { type: "remove_many"; ids: string[] }
  | { type: "update_many"; ids: string[]; changes: Partial<T> };

/**
 * Convenience wrapper around optimisticMutation for list CRUD operations.
 * Applies the specified operation to every matching cache entry that holds a `T[]`.
 */
export async function optimisticListMutation<T extends { id: string }>({
  queryClient,
  queryPredicate,
  operation,
  mutationFn,
  onSettled,
}: {
  queryClient: QueryClient;
  queryPredicate: QueryPredicate;
  operation: ListOperation<T>;
  mutationFn: () => Promise<unknown>;
  onSettled?: () => void;
}): Promise<void> {
  const updater = (oldData: T[]): T[] => {
    if (!Array.isArray(oldData)) return oldData;
    switch (operation.type) {
      case "update":
        return oldData.map((item) =>
          item.id === operation.id ? { ...item, ...operation.changes } : item,
        );
      case "remove":
        return oldData.filter((item) => item.id !== operation.id);
      case "remove_many": {
        const idSet = new Set(operation.ids);
        return oldData.filter((item) => !idSet.has(item.id));
      }
      case "update_many": {
        const idSet = new Set(operation.ids);
        return oldData.map((item) =>
          idSet.has(item.id) ? { ...item, ...operation.changes } : item,
        );
      }
    }
  };

  return optimisticMutation<T[]>({
    queryClient,
    queryPredicate,
    updater,
    mutationFn,
    onSettled,
  });
}

/**
 * Fire-and-forget optimistic list update: applies the cache change immediately,
 * runs the mutation, and invalidates on settle — without blocking the caller.
 * Errors are caught internally (rollback already happened inside optimisticMutation).
 */
export function fireOptimisticListUpdate<T extends { id: string }>({
  queryClient,
  queryPredicate,
  operation,
  mutationFn,
  onSettled,
}: {
  queryClient: QueryClient;
  queryPredicate: QueryPredicate;
  operation: ListOperation<T>;
  mutationFn: () => Promise<unknown>;
  onSettled?: () => void;
}): void {
  optimisticListMutation<T>({
    queryClient,
    queryPredicate,
    operation,
    mutationFn,
    onSettled,
  }).catch(() => {
    // Rollback already happened inside optimisticMutation.
  });
}
